import React, { useState, useContext, useMemo } from 'react';
import classNames from 'classnames';
import SegmentedToggle from 'components/SegmentedToggle';
import AnalitycsCardContent from './components/AnalitycsCardContent';
import { Loader } from 'ncoded-component-library';
import { TFunction, useTranslation } from 'react-i18next';
import DataRoomContext from '../../providers/DataRoom/DataRoom.context';
import { AnaliticsResponse } from '../../dataRoomApi/questions-and-answers/sell';
import ReportsEmptyState from '../../subrouters/Reports/pages/Reports/components/ReportsEmptyState';
import useQAndAChartQuery from './hooks/useQAndAChartQuery';
import TimelineTag from '../../subrouters/Reports/pages/Overview/components/TimelineTag';

import './QAndAChart.styles.scss';

type QAndAChartProps = {
  className?: string;
};

type TilesType = {
  text: string;
  secondaryText: string;
};

type ChartValueType = {
  text: string;
  count: number;
};

export type StagesStats = {
  tiles: Array<TilesType>;
  chartValues: Array<ChartValueType>;
  min: number;
  max: number;
};

const DEFAULT_MIN = 0;
const DEFAULT_MAX = 10;

const SUFIX_MAP: Partial<Record<keyof AnaliticsResponse, string>> = {
  totalPercentOfPublishedQuestions: '%',
  totalAverageMinutesToPublish: 'd',
};

const createStats = (
  data: AnaliticsResponse,
  t: TFunction<'translation', undefined>,
): StagesStats =>
  Object.entries(data || {}).reduce((acc, [key, val]) => {
    if (key !== 'graph') {
      if (key === 'totalNumberOfSubjects') return acc;
      return {
        ...acc,
        tiles: [
          ...(acc.tiles ? (acc as any).tiles : []),
          {
            text: `${val}
             ${
               SUFIX_MAP[key as keyof AnaliticsResponse]
                 ? SUFIX_MAP[key as keyof AnaliticsResponse]
                 : ''
             }`,
            secondaryText: t(key, {
              count: (data as any).totalNumberOfSubjects,
            }),
          },
        ],
      };
    } else {
      return {
        ...acc,
        min: (val as any).min || DEFAULT_MIN,
        max: (val as any).max || DEFAULT_MAX,
        chartValues: ((val as any).data as any).map(({ key, value }: any) => ({
          text: key,
          count: value,
        })),
      };
    }
  }, {} as StagesStats);

const QAndAChart: React.FC<QAndAChartProps> = (props) => {
  const { className } = props;

  const [isSubjectShown, setIsSubjectShown] = useState(false);

  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);

  const { t } = useTranslation();

  const classes = classNames('q-and-a-chart', className);

  const { data, isLoading } = useQAndAChartQuery({
    dataRoomId,
  });

  const stageStats = useMemo(
    () => createStats(data?.stageData, t),
    [data?.stageData, t],
  );

  const subjectStats = useMemo(
    () => createStats(data?.subjectData, t),
    [data?.subjectData, t],
  );

  const emptyStage = useMemo(
    () => !stageStats || stageStats.chartValues?.every((el) => !el.count),
    [stageStats],
  );
  const emptySubject = useMemo(
    () => !subjectStats || subjectStats.chartValues?.every((el) => !el.count),
    [subjectStats],
  );

  return (
    <div className={classes}>
      <div className="q-and-a-chart__heading">
        <p>{t(isSubjectShown ? 'activityBySubject' : 'activityOverall')}</p>
        <TimelineTag />
        <SegmentedToggle
          options={[
            { label: t('stage'), value: false },
            { label: t('subject'), value: true },
          ]}
          selectedValue={isSubjectShown}
          onChange={(value) => setIsSubjectShown(!!value)}
        />
      </div>

      {isLoading ? (
        <Loader />
      ) : (
        <div
          className={classNames('q-and-a-chart__content', {
            translate: isSubjectShown,
          })}
        >
          {emptyStage ? (
            <ReportsEmptyState />
          ) : (
            <AnalitycsCardContent chartAndTileStats={stageStats} />
          )}
          {emptySubject ? (
            <ReportsEmptyState />
          ) : (
            <AnalitycsCardContent chartAndTileStats={subjectStats} />
          )}
        </div>
      )}
    </div>
  );
};

export default QAndAChart;
